import { ApiEndpoint } from './apiTypes';

/**
 * Runner do "Try it": dispara a requisição real do endpoint, dentro do dashboard.
 *
 * A autenticação segue o tipo do endpoint, para não mentir sobre como a API funciona:
 * - INTEGRATION_TOKEN: a pessoa cola o token (ele não fica salvo em lugar nenhum aqui).
 * - TENANT_ID: X-TENANT-ID (por padrão o tenant da sessão) + o Authorization da sessão, porque
 *   a rota passa pelo gateway.
 * - SESSION: só o Authorization da sessão.
 * - PUBLIC: sem credencial.
 *
 * Método que altera estado pede confirmação — mas isso é decidido na tela; aqui só dispara.
 */

export type TryItInputs = {
  /** Valores dos parâmetros de rota (:nome). */
  pathValues?: Record<string, string>;
  /** Valores dos parâmetros de query. */
  queryValues?: Record<string, string>;
  /** Corpo cru, quando houver. */
  body?: string;
  /** Token de integração colado pela pessoa (só para auth INTEGRATION_TOKEN). */
  integrationToken?: string;
  /** X-TENANT-ID; se vazio, cai no tenant da sessão. */
  tenantId?: string;
};

export type TryItResult = {
  status: number;
  ok: boolean;
  body: string;
  durationMs: number;
};

const SESSION_TOKEN_KEY = 'auth-token';
const TENANT_KEY = 'api-key';

const readStorage = (key: string): string => {
  try {
    return localStorage.getItem(key) || '';
  } catch {
    return '';
  }
};

/**
 * Monta a URL final: troca :nome pelos valores e junta a query não-vazia.
 * Pura de propósito, para o teste prender o comportamento sem rede.
 */
export const buildRequestUrl = (
  baseUrl: string,
  endpoint: ApiEndpoint,
  inputs: TryItInputs,
): string => {
  let path = endpoint.path;

  (endpoint.pathParams ?? []).forEach((param) => {
    const value = inputs.pathValues?.[param.name] ?? '';
    path = path.replace(`:${param.name}`, encodeURIComponent(value));
  });

  const pairs = (endpoint.query ?? [])
    .map((param) => [param.name, inputs.queryValues?.[param.name] ?? ''] as const)
    .filter(([, value]) => value !== '');

  const search = pairs
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');

  return `${baseUrl}${path}${search ? `?${search}` : ''}`;
};

const headersFor = (endpoint: ApiEndpoint, inputs: TryItInputs): Record<string, string> => {
  const headers: Record<string, string> = { Accept: 'application/json', Platform: 'DASHBOARD' };
  const session = readStorage(SESSION_TOKEN_KEY);

  switch (endpoint.auth) {
    case 'INTEGRATION_TOKEN':
      if (inputs.integrationToken) headers.Authorization = `Bearer ${inputs.integrationToken}`;
      break;
    case 'TENANT_ID':
      headers['X-TENANT-ID'] = inputs.tenantId || readStorage(TENANT_KEY);
      if (session) headers.Authorization = `Bearer ${session}`;
      break;
    case 'SESSION':
      if (session) headers.Authorization = `Bearer ${session}`;
      break;
    default:
      break;
  }

  if (endpoint.bodyExample) headers['Content-Type'] = 'application/json';

  return headers;
};

const prettyBody = async (response: Response): Promise<string> => {
  const text = await response.text();
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
};

export const runEndpoint = async (
  baseUrl: string,
  endpoint: ApiEndpoint,
  inputs: TryItInputs,
): Promise<TryItResult> => {
  const url = buildRequestUrl(baseUrl, endpoint, inputs);
  const startedAt = Date.now();

  const response = await fetch(url, {
    method: endpoint.method,
    headers: headersFor(endpoint, inputs),
    ...(endpoint.bodyExample && endpoint.method !== 'GET' && inputs.body ? { body: inputs.body } : {}),
  });

  return {
    status: response.status,
    ok: response.ok,
    body: await prettyBody(response),
    durationMs: Date.now() - startedAt,
  };
};
